import { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";

import DstButton from "../../../../../../components/DstButton/DstButton";
import Popup from "../../../../../../components/Popup/Popup";
import translate from "../../../../../../services/Translate";
import { TutorredCohortSkillBlock } from "../../UserTutors.d";
import { TutorFormModalProps } from "./TutorFormModal.d";
import { fetchUserTutorredCohortSkillBlocks, saveUserTutorredCohortForm } from "./TutorFormModal.function";
import "./TutorFormModal.scss";

const _professionalAwarenessCriteria = [
  "PUNCTUALITY",
  "TEAM_INTEGRATION",
  "FOLLOW_INSTRUCTIONS",
  "POLITENESS",
  "WORK_INVOLVEMENT",
  "RESPECT_EQUIPMENT",
];

const _skillLevels = ["BEGINNER", "EXECUTANT", "PROFESSIONAL", "EXPERT"] as const;

const TutorFormModal = ({ cohortId, form, language, onClose, onSubmitted, userId }: TutorFormModalProps) => {
  const [skillBlocks, setSkillBlocks] = useState<TutorredCohortSkillBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [professionalAwarenessLevels, setProfessionalAwarenessLevels] = useState<Record<number, string>>({});
  const [skillBlockLevels, setSkillBlockLevels] = useState<Record<number, string>>({});
  const [finalComment, setFinalComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSkillBlocks([]);
    setLoading(true);
    setProfessionalAwarenessLevels({});
    setSkillBlockLevels({});
    setFinalComment("");
    setSaving(false);

    if (form && cohortId) {
      fetchUserTutorredCohortSkillBlocks(userId, cohortId, language, setSkillBlocks, setLoading);
    }
  }, [cohortId, form, language, userId]);

  const _saveForm = () => {
    if (!form || !cohortId) return;

    const awarenessValues = _professionalAwarenessCriteria.map(
      (_criterion, index) => professionalAwarenessLevels[index] === "acquired"
    );
    const extraComment = finalComment.trim();

    saveUserTutorredCohortForm(
      userId,
      cohortId,
      {
        grade_type: form.form_key === "half" ? "half_cursus" : "ended_cursus",
        being_on_time: awarenessValues[0],
        working_as_team: awarenessValues[1],
        following_guidelines: awarenessValues[2],
        showing_courtesy: awarenessValues[3],
        taking_ownership_of_the_work: awarenessValues[4],
        taking_care_of_materials: awarenessValues[5],
        skill_blocks: skillBlocks.map((skillBlock, index) => ({
          content: skillBlock.content,
          rating: _skillLevels.indexOf(skillBlockLevels[index].toUpperCase() as typeof _skillLevels[number]) + 1,
        })),
        ...(extraComment && { extra_comment: extraComment }),
      },
      language,
      setSaving,
      onSubmitted
    );
  };

  return (
    <Popup show={!!form} onClose={onClose} modalTitle={form?.name || ""} scrollable>
      {loading ? (
        <Spinner className="d-block blue-font my-5 mx-auto" />
      ) : (
        <div className="d-flex flex-column gap-4">
          <section>
            <h3 className="m-bold mb-2">
              {translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.PROFESSIONAL_AWARENESS.TITLE")}
            </h3>
            {_professionalAwarenessCriteria.map((criterion, index) => (
              <div
                className="d-flex align-items-center justify-content-between gap-3 p-3 mb-2 border rounded-2 off-white-background"
                key={criterion}
              >
                <Form.Label className="s-regular mb-0">
                  {translate(
                    language,
                    `PAGES.USER.TUTOR.SKILL_BLOCK_FORM.PROFESSIONAL_AWARENESS.CRITERIA.${criterion}`
                  )}
                </Form.Label>
                <div className="d-flex flex-shrink-0 gap-3">
                  {(["NOT_ACQUIRED", "ACQUIRED"] as const).map((result) => {
                    const value = result.toLowerCase();
                    return (
                      <Form.Check className="d-flex align-items-center gap-1 ps-0 mb-0" key={result}>
                        <Form.Check.Input
                          className="m-0"
                          id={`professional-awareness-${index}-${value}`}
                          name={`professional-awareness-${index}`}
                          type="radio"
                          value={value}
                          checked={professionalAwarenessLevels[index] === value}
                          onChange={(event) =>
                            setProfessionalAwarenessLevels((levels) => ({
                              ...levels,
                              [index]: event.target.value,
                            }))
                          }
                        />
                        <Form.Check.Label
                          className="s-regular lh-1"
                          htmlFor={`professional-awareness-${index}-${value}`}
                        >
                          {translate(language, `PAGES.USER.TUTOR.SKILL_BLOCK_FORM.PROFESSIONAL_AWARENESS.${result}`)}
                        </Form.Check.Label>
                      </Form.Check>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          <section>
            <h3 className="m-bold mb-2">{translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.SKILLS.TITLE")}</h3>
            <div className="skill-level-guidance s-regular p-3 mb-3 border-start border-3 rounded-1 off-white-background">
              <span className="s-bold">
                {translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.SKILLS.GUIDANCE_TITLE")}
              </span>
              <ol className="mb-0 mt-2 ps-4">
                {_skillLevels.map((level) => (
                  <li className="mb-1" key={level}>
                    <span className="s-bold">
                      {translate(language, `PAGES.USER.TUTOR.SKILL_BLOCK_FORM.LEVELS.${level}`)} :{" "}
                    </span>
                    {translate(language, `PAGES.USER.TUTOR.SKILL_BLOCK_FORM.SKILLS.GUIDANCE.${level}`)}
                  </li>
                ))}
              </ol>
            </div>

            <div className="d-flex flex-column gap-3">
              {skillBlocks.map((skillBlock, index) => (
                <div key={`${skillBlock.name}-${index}`}>
                  <Form.Label className="s-bold text-pre-line">{skillBlock.content}</Form.Label>
                  <Form.Select
                    value={skillBlockLevels[index] || ""}
                    onChange={(event) => setSkillBlockLevels((levels) => ({ ...levels, [index]: event.target.value }))}
                  >
                    <option value="">{translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.SELECT_LEVEL")}</option>
                    {_skillLevels.map((level) => (
                      <option key={level} value={level.toLowerCase()}>
                        {translate(language, `PAGES.USER.TUTOR.SKILL_BLOCK_FORM.LEVELS.${level}`)}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
            </div>
          </section>

          <Form.Group>
            <Form.Label className="m-bold">
              {translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.FINAL_COMMENT.LABEL")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={finalComment}
              placeholder={translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.FINAL_COMMENT.PLACEHOLDER")}
              onChange={(event) => setFinalComment(event.target.value)}
            />
          </Form.Group>

          <div className="d-flex justify-content-end mt-3">
            <DstButton
              btnWidth="100px"
              value={translate(language, "PAGES.USER.TUTOR.SKILL_BLOCK_FORM.SUBMIT")}
              clickFunction={_saveForm}
              loading={saving}
              disabled={
                saving ||
                Object.keys(professionalAwarenessLevels).length < _professionalAwarenessCriteria.length ||
                !skillBlocks.length ||
                skillBlocks.some((_skillBlock, index) => !skillBlockLevels[index])
              }
            />
          </div>
        </div>
      )}
    </Popup>
  );
};

export default TutorFormModal;
